import { AddresseeGender, SpeakerGender, TranslationContext } from "./route";
// Remove server-only import
// import { getTranslations } from 'next-intl/server';

// Define a translator type that matches the shape of what we need
export type Translator = (key: string, params?: Record<string, any>) => string;

/**
 * Generates a human-readable summary of translation settings from a TranslationContext object
 */
export function getTranslationSettingsSummaryFromCtx(
  context: TranslationContext,
  t?: Translator
): string | undefined {
  const formality = context.isFormal === true ? 'formal' :
    context.isFormal === false ? 'informal' :
      undefined;

  const addresseeCount = context.isGroup === true ? 'multiple' :
    context.isGroup === false ? 'single' :
      undefined;

  // Call the original function with the extracted values
  return getTranslationSettingsSummary(
    formality,
    context.speakerGender,
    addresseeCount,
    context.addresseeGender,
    t
  );
}

/**
 * Generates a human-readable summary of translation settings
 */
export function getTranslationSettingsSummary(
  formality?: string,
  speakerGender?: SpeakerGender,
  addresseeCount?: string,
  addresseeGender?: AddresseeGender,
  translator?: Translator
): string | undefined {
  // Use provided translator or fallback
  const t = translator || createFallbackTranslator();

  const parts = [];

  if (formality) {
    parts.push(t('formality', { style: t(`${formality}Formality`) }));
  }

  if (speakerGender) {
    parts.push(t('speaker', {
      gender: t(`${speakerGender}Gender`)
    }));
  }

  if (addresseeCount) {
    const isPlural = addresseeCount === 'multiple';

    if (addresseeGender) {
      if (isPlural) {
        parts.push(t('addressingGroup', {
          gender: t(`${addresseeGender}GroupGender`)
        }));
      } else {
        parts.push(t('addressingSinglePerson'));
        if (addresseeGender !== "mixed") {
          parts.push(t('personGender', {
            gender: t(`${addresseeGender}Gender`)
          }));
        }
      }
    } else {
      parts.push(isPlural ? t('addressingGenericGroup') : t('addressingGenericPerson'));
    }
  } else if (addresseeGender && addresseeGender !== "mixed") {
    parts.push(t('addressingGendered', {
      gender: t(`${addresseeGender}OneOrMoreGender`)
    }));
  }

  if (parts.length === 0)
    return undefined;

  const summary = parts.join(t('separator'));

  return t('format', { summary: summary.charAt(0).toUpperCase() + summary.slice(1) });
}

// TODO: Do we even need this? -->
// Fallback translator that uses English strings (directly copied from en.json)
function createFallbackTranslator(): Translator {
  const translations: Record<string, string> = {
    "formality": "conversation is {style}",
    "formalFormality": "formal",
    "informalFormality": "informal",
    "speaker": "speaker is {gender}",
    "masculineGender": "male",
    "feminineGender": "female",
    "neutralGender": "of neutral gender",
    "addressingGroup": "addressing a group of {gender}",
    "addressingSinglePerson": "addressing a single person",
    "personGender": "who is {gender}",
    "addressingGenericGroup": "addressing a group of people",
    "addressingGenericPerson": "addressing a single person",
    "addressingGendered": "addressing a {gender}",
    "masculineGroupGender": "males",
    "feminineGroupGender": "females",
    "mixedGroupGender": "people of mixed gender",
    "neutralGroupGender": "people of neutral gender",
    "masculineOneOrMoreGender": "male(s)",
    "feminineOneOrMoreGender": "female(s)",
    "mixedOneOrMoreGender": "person (or people) of mixed gender",
    "neutralOneOrMoreGender": "person (or people) of neutral gender",
    "separator": ", ",
    "format": "{summary}."
  };

  return (key: string, params?: Record<string, any>) => {
    let text = translations[key] || key;
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        text = text.replace(`{${key}}`, value);
      });
    }
    return text;
  };
}